/**
 * dateEntry — typed MM/DD/YYYY date entry (Collette Cruse life-ring report
 * 2026-10-16: she could not type a date of birth with the number keypad in
 * the browser date control; Bill approved 2026-10-16).
 *
 * Pure helpers behind <DateTextInput>:
 *   - formatDateTyping: digits (and optional slashes) -> "MM/DD/YYYY" as typed,
 *     slashes added automatically, 1- or 2-digit month and day accepted.
 *   - parseUsDate: a complete entry -> ISO "YYYY-MM-DD" (via jxDates
 *     toNamedJsonDate), or a plain-language problem.
 *   - isoToUsDate: stored ISO (or any jxDates-readable value) -> "MM/DD/YYYY".
 */
import { toNamedJsonDate } from './jxDates';

/**
 * Format what the officer typed. Only digits and slashes count; everything
 * else is dropped. A slash is added when the next digit cannot belong to the
 * current part (month "5" + "3" -> "5/3", day "4" + "1" -> "4/1"), or when a
 * 2-digit month/day is followed by another digit. A typed slash ends a
 * 1-digit month or day. The year is at most 4 digits.
 *
 * A slash is only shown once a digit (or a typed slash) follows the part, so
 * Backspace over a slash never gets stuck.
 */
export function formatDateTyping(raw: string): string {
  const parts: [string, string, string] = ['', '', ''];
  let idx = 0;
  for (const ch of raw) {
    if (ch === '/' || ch === '-' || ch === '.' || ch === ' ') {
      if (idx < 2 && parts[idx].length > 0) {
        idx += 1;
      }
      continue;
    }
    if (ch < '0' || ch > '9') continue;
    if (idx < 2) {
      const cur = parts[idx];
      const max = idx === 0 ? 12 : 31;
      if (cur.length === 2 || (cur.length === 1 && Number(cur + ch) > max)) {
        idx += 1;
      }
    }
    if (idx === 2 && parts[2].length >= 4) continue;
    parts[idx] += ch;
  }
  let out = parts[0];
  if (idx >= 1) out += '/' + parts[1];
  if (idx >= 2) out += '/' + parts[2];
  return out;
}

export type ParsedUsDate =
  | { kind: 'empty' }
  | { kind: 'incomplete' }
  | { kind: 'invalid'; message: string }
  | { kind: 'ok'; iso: string };

export interface ParseUsDateOptions {
  /** Earliest year accepted (default 1900). */
  minYear?: number;
  /** Reject dates after today (e.g. a date of birth). */
  notInFuture?: boolean;
  /** "Today" for the future check (tests). */
  today?: Date;
}

/** Parse a typed "M/D/YYYY" / "MM/DD/YYYY" entry. ISO only when complete and real. */
export function parseUsDate(text: string, opts: ParseUsDateOptions = {}): ParsedUsDate {
  const t = (text ?? '').trim();
  if (t === '') return { kind: 'empty' };
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  if (!m) return { kind: 'incomplete' };
  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12) return { kind: 'invalid', message: 'The month must be 1 to 12.' };
  if (day < 1 || day > 31) return { kind: 'invalid', message: 'The day must be 1 to 31.' };
  const minYear = opts.minYear ?? 1900;
  if (year < minYear) return { kind: 'invalid', message: `The year must be ${minYear} or later.` };
  const iso = toNamedJsonDate(`${month}/${day}/${year}`);
  if (!iso) return { kind: 'invalid', message: 'That date does not exist. Check the month and day.' };
  if (opts.notInFuture) {
    const now = opts.today ?? new Date();
    const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (iso > todayIso) return { kind: 'invalid', message: 'The date cannot be in the future.' };
  }
  return { kind: 'ok', iso };
}

/** Display a stored date as "MM/DD/YYYY" ('' when blank or unreadable). */
export function isoToUsDate(value: string | null | undefined): string {
  const iso = toNamedJsonDate(value ?? '');
  if (!iso) return '';
  const [y, mo, d] = iso.split('-');
  return `${mo}/${d}/${y}`;
}
